import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { palette } from "../../../../assets/styles/Palette";
import { H4, Body2 } from "../../../../assets/styles/Typography";
import MoleculeEditableMarkdown from "../../../CreateInfoContentPage/components/molecules/MoleculeEditableMarkdown";

const MoleculeMarkdownEditPopup = ({
  content,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState("");
  const [markdown, setMarkdown] = useState(["", ""]);
  const [markdownTitle, setMarkdownTitle] = useState(["", ""]);
  const [isEditing, setIsEditing] = useState(false);
  const [isTempSaved, setIsTempSaved] = useState(false);

  // 초기 데이터 로드
  useEffect(() => {
    if (content) {
      setTitle(content.dataAnalysis?.title || "");
      setMarkdown(content.contentGeneratorMarkdown || ["", ""]);
      
      const titleArray = Array.isArray(content.contentGeneratorTitle) &&
        content.contentGeneratorTitle.length > 0
        ? Array.isArray(content.contentGeneratorTitle[0])
          ? content.contentGeneratorTitle[0]
          : content.contentGeneratorTitle
        : [];
      setMarkdownTitle([titleArray[0] || "", titleArray[1] || ""]);
      setIsTempSaved(false);
    }
  }, [content]);

  const handleMarkdownTempSave = (data) => {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      setMarkdown(data.markdown || ["", ""]);
      setMarkdownTitle(data.title || ["", ""]);
    } else {
      setMarkdown(data || ["", ""]);
    }
    setIsTempSaved(true);
  };

  const handleSaveClick = () => {
    onSave({ title, markdown, markdownTitle });
  };

  return (
    <MarkdownEditPopupOverlay>
      <MarkdownEditPopupContainer>
        <MarkdownEditPopupHeader>
          <H4 color="gray800">콘텐츠 내용 수정</H4>
          <CloseButton onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#666666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </MarkdownEditPopupHeader>
        <MarkdownEditPopupBody>
          {/* 제목 수정 필드 */}
          <TitleEditSection>
            <TitleLabel>
              <Body2 color="gray700">콘텐츠 제목</Body2>
            </TitleLabel>
            <TitleInput
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="콘텐츠 제목을 입력하세요"
              maxLength={50}
            />
          </TitleEditSection>

          {/* 마크다운 수정 컴포넌트 */}
          <MoleculeEditableMarkdown
            markdown={markdown}
            title={markdownTitle}
            onSave={handleMarkdownTempSave}
            onRegenerate={null}
            onEditingChange={setIsEditing}
            maxLines={
              content.contentsType === "link"
                ? [12, 10]
                : [12, 12]
            }
            maxChars={
              content.contentsType === "link"
                ? [300, 200]
                : [300, 300]
            }
            showRegenerateButton={false}
            editableTitle={true}
          />
        </MarkdownEditPopupBody>
        <MarkdownEditPopupFooter>
          <ButtonRow>
            <ButtonCancel onClick={onClose}>
              <ButtonTextCancel>취소</ButtonTextCancel>
            </ButtonCancel>
            <ButtonPrimary
              onClick={handleSaveClick}
              disabled={
                isEditing ||
                (!isTempSaved &&
                  title === (content?.dataAnalysis?.title || ""))
              }
            >
              <ButtonText>저장</ButtonText>
            </ButtonPrimary>
          </ButtonRow>
        </MarkdownEditPopupFooter>
      </MarkdownEditPopupContainer>
    </MarkdownEditPopupOverlay>
  );
};

export default MoleculeMarkdownEditPopup;

const MarkdownEditPopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
`;

const MarkdownEditPopupContainer = styled.div`
  background: ${palette.white};
  border-radius: 15px;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 4px 4px 30px rgba(0, 0, 0, 0.15);
`;

const MarkdownEditPopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid ${palette.outlineGray};
`;

const MarkdownEditPopupBody = styled.div`
  padding: 32px;
  overflow-y: auto;
  flex: 1;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

const TitleEditSection = styled.div`
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleLabel = styled.div`
  display: flex;
  align-items: center;
`;

const TitleInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${palette.outlineGray};
  border-radius: 8px;
  font-size: 16px;
  font-family: Pretendard;
  color: ${palette.gray800};
  background: ${palette.white};

  &:focus {
    outline: none;
    border-color: ${palette.primary};
  }

  &::placeholder {
    color: ${palette.gray500};
  }
`;

const MarkdownEditPopupFooter = styled.div`
  padding: 24px 32px;
  border-top: 1px solid ${palette.outlineGray};
  display: flex;
  justify-content: flex-end;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const ButtonCancel = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: ${palette.white};
  border: 1px solid ${palette.primary};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: ${palette.gray50};
  }
`;

const ButtonPrimary = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: ${palette.primary};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: ${palette.primaryDark};
  }

  &:disabled {
    background: #9e9e9e;
    cursor: not-allowed;
  }
`;

const ButtonText = styled.span`
  color: ${palette.white};
  font-size: 17px;
  font-weight: 400;
  font-family: Pretendard;
`;

const ButtonTextCancel = styled.span`
  color: ${palette.primary};
  font-size: 17px;
  font-weight: 400;
  font-family: Pretendard;
`;
